import * as Sentry from '@sentry/react';
import { App, Modal } from 'antd';
import { fileTypeFromBuffer } from 'file-type';
import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import FilesUploadErrorForm from '../../components/Forms/FilesUploadErrorForm';
import FileUploadOptionsForm from '../../components/Forms/FileUploadOptionsForm';
import { ApiErrorEnum } from '../../enums/ApiErrorEnum';
import { FileUploadStatusEnum } from '../../enums/FileUploadStatusEnum';
import { getApiErrorCode } from '../../utils/apiErrorCode';
import { UploadFileActionEnum } from '../../enums/UploadFileActionEnum';
import { useAuth } from '../../hooks/useAuth';
import { useBatches } from '../../hooks/useBatches';
import type FileInterface from '../../interfaces/File';
import type {
  CreateFolderPayload,
  UpdateFilesAccessPayload,
} from '../../interfaces/FileApiService';
import type Group from '../../interfaces/Group';
import type GroupedUploadItem from '../../interfaces/GroupedUploadItem';
import type UploadFile from '../../interfaces/UploadFile';
import type User from '../../interfaces/User';
import { FileService } from '../../services/fileApiService';
import { TransferService, type TransferUploadOptions } from '../../services/transferApiService';
import { useAppStore } from '../../store/store';
import { createIncrementalSha256Hasher, dateToTicks, generateUUID } from '../../utils/utils';
import { isTransientNetworkError } from '../../utils/isTransientNetworkError';
import {
  clearUploadQueue,
  isQueuePersistable,
  loadUploadQueue,
  persistUploadQueue,
} from '../../db/uploadQueue';

/**
 * Extends FileInterface with a quantity property for grouping and counting purposes.
 */
interface GroupedExistingFile extends FileInterface {
  quantity: number;
}

function incrementGroupedExistingFileEntry(
  groupedFiles: Record<string, GroupedExistingFile>,
  key: string,
  sourceFile: FileInterface,
): void {
  const previousGroupedFile = groupedFiles[key];
  groupedFiles[key] = {
    ...(previousGroupedFile ?? { ...sourceFile, quantity: 0 }),
    quantity: (previousGroupedFile?.quantity ?? 0) + 1,
  };
}

/**
 * Defines the contract for the FileUploadContext's value.
 */
interface FileUploadContextType {
  uploadFiles: (
    items: FileList | DataTransferItemList | UploadFile[],
    skipTreeBuild?: boolean,
    folderUid?: number,
    cufi?: string,
    isDestinationFolderPasswordProtected?: boolean,
    destinationFolderName?: string,
  ) => void;
  uploadFilesQueue: UploadFile[];
  uploadGroupedFilesQueue: GroupedUploadItem[];
  uploadQuickShareGroupedFilesQueue: GroupedUploadItem[];
  uploadQuickShareGroupedFilesQueueWithoutRootFolder: GroupedUploadItem[];
  uploadingFilesAmount: number;
  uploadedFilesAmount: number;
  totalGroupedFilesAmount: number;
  totalQuickShareGroupedFilesAmount: number;
  uploadedGroupedFilesAmount: number;
  uploadedQuickShareGroupedFilesAmount: number;
  failedGroupedFilesAmount: number;
  isUploadingFiles: boolean;
  isUploadingQuickShareFiles: boolean;
  areFoldersPresentsInFilesQueue: boolean;
  getFailedFilesInFolder: (uuid: string) => UploadFile[];
  isFileInsideFolder: (file: UploadFile) => boolean;
  buildFileTreeFromUploadFiles: (files: UploadFile[]) => UploadFile[];
  quickShareForm: QuickShareFormState;
  getFilesToEnqueue: (
    fileTree: UploadFile[],
    parentUid?: number | null,
    parentUUID?: string | null,
  ) => UploadFile[];
  groupUploadItems: (
    files: UploadFile[],
    onlyQuickShare?: boolean,
    startDepth?: number,
  ) => GroupedUploadItem[];
  emptyUploadFilesQueue: () => void;
  emptyQuickShareUploadFilesQueue: () => void;
  cancelUploadItem: (rootId: string) => void;
  getFileTree: (items: FileList | DataTransferItemList) => Promise<UploadFile[]>;
  onNewFileUploaded: (callback: (file: FileInterface, isReplacing: boolean) => void) => void;
  offNewFileUploaded: (callback: (file: FileInterface, isReplacing: boolean) => void) => void;
  handleQuickShareInputChange: (field: keyof QuickShareFormState, value: any) => void;
  handleShareQuickShareFiles: () => void;
  failedUploadQuickShareFiles: UploadFile[];
  handleOpenUploadingFilesErrorsModal: (onlyQuickShareFiles: boolean) => void;
  retryFailedUploads: (onlyQuickShare?: boolean) => void;
  isQuickShareUploadFilesCompletedWithoutErrors: boolean;
  setCurrentFolderUid: (uid: number | null) => void;
  quickShareGlobalProgressByBytes: number;
  isNetworkAvailable: boolean;
}

/**
 * State type for the Quick Share form fields.
 */
interface QuickShareFormState {
  users: User[];
  groups: Group[];
  permission: string;
  expiration: string;
  hasMessage: boolean;
  message: string;
}

// Represents a possible upload response from server.
type UploadResponse = { cufi: string; version: number } | FileInterface;

// Instantiate services used in upload logic.
const transferService = new TransferService();
const fileService = new FileService();

/**
 * Default/initial context value for FileUploadContext.
 */
export const FileUploadContext = createContext<FileUploadContextType>({
  uploadFiles: () => {},
  uploadFilesQueue: [],
  uploadGroupedFilesQueue: [],
  uploadQuickShareGroupedFilesQueue: [],
  uploadQuickShareGroupedFilesQueueWithoutRootFolder: [],
  uploadingFilesAmount: 0,
  uploadedFilesAmount: 0,
  totalGroupedFilesAmount: 0,
  uploadedGroupedFilesAmount: 0,
  uploadedQuickShareGroupedFilesAmount: 0,
  totalQuickShareGroupedFilesAmount: 0,
  failedGroupedFilesAmount: 0,
  isUploadingFiles: false,
  isUploadingQuickShareFiles: false,
  buildFileTreeFromUploadFiles: () => [],
  areFoldersPresentsInFilesQueue: false,
  getFilesToEnqueue: () => [],
  groupUploadItems: () => [],
  getFailedFilesInFolder: () => [],
  isFileInsideFolder: () => false,
  emptyUploadFilesQueue: () => {},
  emptyQuickShareUploadFilesQueue: () => {},
  cancelUploadItem: () => {},
  getFileTree: async () => [],
  onNewFileUploaded: () => {},
  offNewFileUploaded: () => {},
  quickShareForm: {
    users: [],
    groups: [],
    permission: '0',
    expiration: '1',
    hasMessage: true,
    message: '',
  },
  handleQuickShareInputChange: () => {},
  handleShareQuickShareFiles: () => {},
  failedUploadQuickShareFiles: [],
  handleOpenUploadingFilesErrorsModal: () => {},
  retryFailedUploads: () => {},
  isQuickShareUploadFilesCompletedWithoutErrors: false,
  setCurrentFolderUid: () => {},
  quickShareGlobalProgressByBytes: 0,
  isNetworkAvailable: true,
});

// Used to keep current folder in non-reactive form.
let currentFolderUid: number | null = null;

/**
 * Provides all context functionality and state for uploading files throughout the application.
 * Handles queue management, upload logic, file/folder tree building, and error sharing for files and Quick Share.
 */
export const FileUploadProvider = ({ children }: { children: ReactNode }) => {
  // Hooks/Services
  const { modal, notification } = App.useApp();
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const location = useLocation();
  const authenticatedUser = useAppStore((s) => s.authenticatedUser);
  const getFilePasswordSession = useAppStore((s) => s.getFilePasswordSession);
  const pendingFilePasswordRequest = useAppStore((s) => s.pendingFilePasswordRequest);
  const setPendingFilePasswordRequest = useAppStore((s) => s.setPendingFilePasswordRequest);
  const setPendingFilePasswordFromMoveForm = useAppStore(
    (s) => s.setPendingFilePasswordFromMoveForm,
  );
  const setPendingFilePasswordFromUpload = useAppStore((s) => s.setPendingFilePasswordFromUpload);
  const { refreshBatches, isPolling: isBatchesPolling } = useBatches();

  // Upload queue state
  const [uploadFilesQueue, setUploadFilesQueue] = useState<UploadFile[]>([]);
  const [isNetworkAvailable, setIsNetworkAvailable] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine,
  );

  // State for Quick Share form
  const [quickShareForm, setQuickShareForm] = useState<QuickShareFormState>({
    users: [],
    groups: [],
    permission: '0',
    expiration: '1',
    hasMessage: true,
    message: '',
  });

  // UI state: controls upload error modal
  const [showOnlyQuickShareFilesUploadingErrors, setShowOnlyQuickShareFilesUploadingErrors] =
    useState(false);

  // Subscribers for uploaded file notifications
  const newFileUploadedSubscribers = useRef(
    new Set<(file: FileInterface, isReplacing: boolean) => void>(),
  );

  // Track if we've already triggered batches refresh after upload completion
  const hasTriggeredBatchesRefresh = useRef(false);

  // Upload options reference (reflects latest selected modal value for replacement mode)
  let uploadOptionAction = useRef<UploadFileActionEnum>(UploadFileActionEnum.Replace);
  let uploadOptionGroupedExistingFiles = useRef<Record<string, GroupedExistingFile>>({});

  /** When upload is blocked by folder password, store state to continue after modal closes. */
  const pendingUploadAfterPasswordRef = useRef<{
    fileTree: UploadFile[];
    targetFolderUid: number | null;
    skipExistingCheck: boolean;
  } | null>(null);

  /** AbortController per file currently uploading (file id -> controller). Used to abort XHR on cancel. */
  const uploadAbortControllersRef = useRef<Map<string, AbortController>>(new Map());
  /** True while uploads were aborted due to network loss (not user cancel). */
  const networkPauseRef = useRef(false);
  const tryStartUploadsRef = useRef<() => void>(() => {});
  const resumeUploadsTimeoutRef = useRef<number | null>(null);
  const uploadSessionIdRef = useRef<string | null>(null);
  const uploadTargetFolderUidRef = useRef<number | null>(null);
  const hasHydratedUploadQueueRef = useRef(false);
  const syncUploadQueueTimeoutRef = useRef<number | null>(null);
  /** Ids of items cancelled by user; used to ignore completion of in-flight requests (e.g. createFolder). */
  const cancelledUploadIdsRef = useRef<Set<string>>(new Set());

  // Constants for upload logic
  const MAX_CONCURRENT_UPLOADS = 5; // Max simultaneous uploads allowed
  const CHUNK_SIZE = 10 * 1024 * 1024; // File chunk size (default 10MB)

  const pauseUploadsForNetwork = useCallback(() => {
    networkPauseRef.current = true;
    uploadAbortControllersRef.current.forEach((controller) => controller.abort());
    uploadAbortControllersRef.current.clear();
    setUploadFilesQueue((previousQueue) =>
      previousQueue.map((uploadItem) =>
        uploadItem.status === FileUploadStatusEnum.Uploading
          ? { ...uploadItem, status: FileUploadStatusEnum.Pending }
          : uploadItem,
      ),
    );
  }, []);

  const purgePersistedUploadQueue = useCallback(async () => {
    const userId = authenticatedUser?.id;
    if (userId == null) return;
    await clearUploadQueue(userId);
    uploadSessionIdRef.current = null;
  }, [authenticatedUser?.id]);

  const scheduleSyncUploadQueueToIdb = useCallback(() => {
    if (syncUploadQueueTimeoutRef.current != null) {
      window.clearTimeout(syncUploadQueueTimeoutRef.current);
    }
    syncUploadQueueTimeoutRef.current = window.setTimeout(() => {
      syncUploadQueueTimeoutRef.current = null;
      const userId = authenticatedUser?.id;
      const sessionId = uploadSessionIdRef.current;
      if (userId == null || sessionId == null) return;

      const isQuickShareSession = uploadFilesQueue.some((item) => item.isQuickShare);
      const quickShareFormEmpty =
        quickShareForm.users.length === 0 &&
        quickShareForm.groups.length === 0 &&
        quickShareForm.message.trim() === '';

      const shouldPersist = isQueuePersistable(uploadFilesQueue, {
        userId,
        pendingFilePasswordRequest,
        quickShareFormEmpty,
        isQuickShareSession,
      });

      if (!shouldPersist) {
        const hasRecoverable = uploadFilesQueue.some(
          (item) =>
            item.status === FileUploadStatusEnum.Pending ||
            item.status === FileUploadStatusEnum.Uploading,
        );
        if (!hasRecoverable) {
          void purgePersistedUploadQueue();
        }
        return;
      }

      void persistUploadQueue(
        {
          sessionId,
          userId,
          isQuickShare: isQuickShareSession,
          quickShareForm,
          targetFolderUid: uploadTargetFolderUidRef.current,
        },
        uploadFilesQueue,
      );
    }, 500);
  }, [
    authenticatedUser?.id,
    pendingFilePasswordRequest,
    purgePersistedUploadQueue,
    quickShareForm,
    uploadFilesQueue,
  ]);

  /**
   * Update the context's current folder UID (used as the default parent for uploads).
   */
  function setCurrentFolderUid(uid: number | null) {
    currentFolderUid = uid;
  }

  /**
   * Mark all descendants (files & folders) of the provided root as failed in the upload queue.
   * Used in error scenarios where an entire subtree cannot be uploaded.
   */
  function markSubtreeFailed(root: UploadFile, errorMsg: string) {
    setUploadFilesQueue((prev) => {
      // Quickly map parentUUID -> children for traversal
      const byParent = new Map<string | null, UploadFile[]>();
      for (const u of prev) {
        const key = u.parentUUID ?? null;
        const arr = byParent.get(key) ?? [];
        arr.push(u);
        byParent.set(key, arr);
      }

      // Traverse using a stack (DFS) to collect all files/folders in the subtree
      const toFail = new Set<string>();
      const stack = [root.id];
      toFail.add(root.id); // Add root iteslf

      while (stack.length) {
        const parent = stack.pop()!;
        const children = byParent.get(parent) ?? [];
        for (const ch of children) {
          if (!toFail.has(ch.id)) {
            toFail.add(ch.id);
            if (ch.isFolder) stack.push(ch.id);
          }
        }
      }

      // Update the failed subtree in the queue: set failed status, block retry and record error.
      return prev.map((u) =>
        toFail.has(u.id)
          ? {
              ...u,
              status: FileUploadStatusEnum.Failed,
              retries: 3,
              skipRetryDisplay: true,
              error: errorMsg,
              progress: u.isFolder ? u.progress : (u.progress ?? 0),
            }
          : u,
      );
    });
  }

  /**
   * Returns the set of all ids in the subtree rooted at rootId (root + all descendants).
   * Uses same parentUUID traversal as markSubtreeFailed.
   */
  function getSubtreeIds(queue: UploadFile[], rootId: string): Set<string> {
    const byParent = new Map<string | null, UploadFile[]>();
    for (const u of queue) {
      const key = u.parentUUID ?? null;
      const arr = byParent.get(key) ?? [];
      arr.push(u);
      byParent.set(key, arr);
    }
    const subtreeIds = new Set<string>();
    const stack = [rootId];
    subtreeIds.add(rootId);
    while (stack.length) {
      const parent = stack.pop()!;
      const children = byParent.get(parent) ?? [];
      for (const ch of children) {
        if (!subtreeIds.has(ch.id)) {
          subtreeIds.add(ch.id);
          if (ch.isFolder) stack.push(ch.id);
        }
      }
    }
    return subtreeIds;
  }

  /**
   * Cancel upload for a single file or folder (and its subtree).
   * If only in queue (Pending): remove from queue. If upload already started: abort request and mark subtree as failed.
   */
  const cancelUploadItem = useCallback(
    (rootId: string) => {
      const root = uploadFilesQueue.find((u) => u.id === rootId);
      if (!root) return;

      const subtreeIds = getSubtreeIds(uploadFilesQueue, rootId);
      const hasUploading = uploadFilesQueue.some(
        (u) => subtreeIds.has(u.id) && u.status === FileUploadStatusEnum.Uploading,
      );

      if (hasUploading) {
        const uploadingInSubtree = uploadFilesQueue.filter(
          (u) => subtreeIds.has(u.id) && u.status === FileUploadStatusEnum.Uploading,
        );
        uploadingInSubtree.forEach((u) => {
          const controller = uploadAbortControllersRef.current.get(u.id);
          if (controller) {
            controller.abort();
            uploadAbortControllersRef.current.delete(u.id);
          }
        });
        subtreeIds.forEach((id) => cancelledUploadIdsRef.current.add(id));
        const errorMsg = t('errors.uploadInterrupted');
        setUploadFilesQueue((prev) =>
          prev.map((u) =>
            subtreeIds.has(u.id) && u.status !== FileUploadStatusEnum.Completed
              ? {
                  ...u,
                  status: FileUploadStatusEnum.Canceled,
                  retries: 3,
                  skipRetryDisplay: true,
                  error: errorMsg,
                }
              : u,
          ),
        );
      } else {
        setUploadFilesQueue((prev) => prev.filter((u) => !subtreeIds.has(u.id)));
      }
    },
    [uploadFilesQueue, t],
  );

  /**
   * Build a hierarchical tree of files and folders from a flat File[] array based on webkitRelativePath.
   *  - Each directory gets an UploadFile node with .isFolder=true and an empty .file property.
   *  - Each file gets an UploadFile node with .isFolder=false and .file property.
   */
  const buildFileTree = (files: File[]): UploadFile[] => {
    const root: UploadFile[] = [];
    const batch = generateUUID();
    let currentRootCorrelationId: string | undefined;

    files.forEach((file) => {
      const relativePath = file.webkitRelativePath || file.name;
      const parts = relativePath.split('/');
      let currentLevel = root;
      let currentPath = '';

      parts.forEach((part, index) => {
        const isFile = index === parts.length - 1;
        const existing = currentLevel.find((item) => item.fileName === part);

        if (existing) {
          currentRootCorrelationId = existing.correlationId ?? currentRootCorrelationId;
          currentLevel = existing.children!;
          // Update current path for next iteration
          // If existing item has relativePath, use it; otherwise reconstruct from currentPath
          currentPath = existing.relativePath || (currentPath ? `${currentPath}/${part}` : part);
        } else {
          // Build the path for this item
          const itemPath = currentPath ? `${currentPath}/${part}` : part;
          const correlationId =
            currentLevel === root ? generateUUID() : (currentRootCorrelationId as string);

          const newItem: UploadFile = {
            id: generateUUID(),
            fileName: part,
            relativePath: itemPath,
            isFolder: !isFile,
            file: isFile ? file : undefined,
            progress: 0,
            status: FileUploadStatusEnum.Pending,
            isQuickShare: location.pathname === '/quick-share',
            retries: 0,
            children: [],
            batch,
            correlationId,
          };
          if (currentLevel === root) currentRootCorrelationId = newItem.correlationId;
          currentLevel.push(newItem);
          if (newItem.isFolder) {
            currentLevel = newItem.children!;
            currentPath = itemPath;
          }
        }
      });
    });

    return root;
  };

  /**
   * Recursively process a FileSystemEntry (file or directory) into a flat File[] array.
   * If a directory, descends into children; if file, creates File with correct mime type.
   */
  const processEntry = (entry: FileSystemEntry, path: string = ''): Promise<File[]> => {
    return new Promise((resolve, reject) => {
      let fileList: File[] = [];
      if (entry.isFile) {
        (entry as FileSystemFileEntry).file(async (file) => {
          let mimeType = file.type;
          if (!mimeType) {
            const arrayBuffer = await file.arrayBuffer();
            const fileType = await fileTypeFromBuffer(arrayBuffer);
            mimeType = fileType?.mime || 'application/octet-stream';
          }
          fileList.push(new File([file], `${path}${file.name}`, { type: mimeType }));
          resolve(fileList);
        }, reject);
      } else if (entry.isDirectory) {
        const reader = (entry as FileSystemDirectoryEntry).createReader();
        reader.readEntries((entries) => {
          const promises = entries.map((ent) => processEntry(ent, `${path}${entry.name}/`));
          Promise.all(promises)
            .then((results) => resolve(results.flat()))
            .catch(reject);
        }, reject);
      }
    });
  };

  /**
   * Traverse a DataTransfer or FileList into a flat File[] (smartly supports drag-drop folders using webkitGetAsEntry).
   */
  const traverseFileTree = async (items: FileList | DataTransferItemList): Promise<File[]> => {
    let fileList: File[] = [];
    const promises = [];
    for (let i = 0; i < items.length; i++) {
      let entry: FileSystemEntry | null = null;
      const item = items[i];
      if (!item) continue;

      if ('webkitGetAsEntry' in item) {
        entry = (item as DataTransferItem).webkitGetAsEntry();
      } else if ('getAsFile' in item) {
        const file = (item as File).webkitRelativePath ? item : (item as File);
        if (file) {
          let mimeType = file.type;
          if (!mimeType) {
            const arrayBuffer = await file.arrayBuffer();
            const fileType = await fileTypeFromBuffer(arrayBuffer);
            mimeType = fileType?.mime || 'application/octet-stream';
          }
          fileList.push(new File([file], file.name, { type: mimeType }));
        }
      }

      if (entry) {
        promises.push(processEntry(entry));
      }
    }
    const results = await Promise.all(promises);
    fileList = fileList.concat(...results.flat());
    return fileList;
  };

  /**
   * Construct a file/folder tree (UploadFile[]) from a flat UploadFile[] referencing parentUUID.
   */
  function buildFileTreeFromUploadFiles(files: UploadFile[]): UploadFile[] {
    const fileMap = new Map<string, UploadFile>();
    files.forEach((file) => {
      file.children = [];
      fileMap.set(file.id, file);
    });
    const root: UploadFile[] = [];
    files.forEach((file) => {
      if (file.parentUUID) {
        const parent = fileMap.get(file.parentUUID);
        if (parent) parent.children!.push(file);
      } else {
        root.push(file);
      }
    });
    return root;
  }

  /**
   * Flatten a hierarchical UploadFile[] tree to a flat array and assign parentUid/parentUUID as needed.
   */
  const getFilesToEnqueue = (
    fileTree: UploadFile[],
    parentUid: number | null = null,
    parentUUID: string | null = null,
  ): UploadFile[] => {
    const queue: UploadFile[] = [];
    const traverse = (nodes: UploadFile[], parentId: number | null, parentUUID: string | null) => {
      nodes.forEach((node) => {
        if (node.parentUid === undefined || node.parentUid === null) node.parentUid = parentId;
        if (node.parentUUID === undefined || node.parentUUID === null) node.parentUUID = parentUUID;
        queue.push(node);
        if (node.isFolder && node.children) {
          traverse(node.children, null, node.id);
        }
      });
    };
    traverse(fileTree, parentUid, parentUUID);
    return queue;
  };

  /**
   * Validates file sizes against the maximum allowed size (license and admin limits) and marks oversized files as failed.
   * Returns the queue with oversized files already marked as failed.
   * Checks license limit first (maxFileSizeLic), then admin limit (maxFileSizeSet).
   */
  const validateAndMarkOversizedFiles = (queue: UploadFile[]): UploadFile[] => {
    // If no limits are set, allow all files
    if (!authenticatedUser?.maxFileSizeLic && !authenticatedUser?.maxFileSizeSet) {
      return queue;
    }

    // Convert limits from MB to bytes
    const licenseLimitBytes = authenticatedUser?.maxFileSizeLic
      ? authenticatedUser.maxFileSizeLic * 1024 * 1024
      : null;
    const adminLimitBytes = authenticatedUser?.maxFileSizeSet
      ? authenticatedUser.maxFileSizeSet * 1024 * 1024
      : null;

    return queue.map((file) => {
      // Only check actual files, not folders
      if (!file.isFolder && file.file) {
        const fileSize = file.file.size;

        // Check license limit first (has priority)
        if (licenseLimitBytes && fileSize > licenseLimitBytes) {
          return {
            ...file,
            status: FileUploadStatusEnum.Failed,
            error: t('errors.fileSizeExceedsLicenseLimit'),
            retries: 3, // Prevent retry attempts
            skipRetryDisplay: true,
            progress: 0,
          };
        }

        // Check admin limit
        if (adminLimitBytes && fileSize > adminLimitBytes) {
          return {
            ...file,
            status: FileUploadStatusEnum.Failed,
            error: t('errors.fileSizeExceedsAdminLimit'),
            retries: 3, // Prevent retry attempts
            skipRetryDisplay: true,
            progress: 0,
          };
        }
      }
      return file;
    });
  };

  /**
   * Add files/folders from the provided tree into the upload queue, setting correct parent info.
   * Validates file sizes before enqueueing to prevent oversized files from starting upload.
   */
  const enqueueFiles = (
    fileTree: UploadFile[],
    parentUid: number | null = null,
    parentUUID: string | null = null,
  ) => {
    uploadSessionIdRef.current = generateUUID();
    uploadTargetFolderUidRef.current = parentUid;
    const queue = getFilesToEnqueue(fileTree, parentUid, parentUUID);
    const validatedQueue = validateAndMarkOversizedFiles(queue);
    setUploadFilesQueue((prevQueue) => [...prevQueue, ...validatedQueue]);
  };

  /**
   * Check for existing files in destination, then either show Replace/NewOnly/KeepBoth modal or enqueue.
   * Used by uploadFiles and by the effect that continues upload after folder password success.
   */
  const checkExistingAndEnqueueOrShowModal = useCallback(
    async (fileTree: UploadFile[], verifiedTargetFolderUid: number | null) => {
      const filesToCheck: string[] = fileTree.map((item) => item.fileName || '');
      const existingFiles = await fileService.checkFilesExists(
        filesToCheck,
        verifiedTargetFolderUid,
      );
      const groupedExistingFiles: Record<string, GroupedExistingFile> = existingFiles.reduce(
        (acc: Record<string, GroupedExistingFile>, file: FileInterface) => {
          incrementGroupedExistingFileEntry(acc, file.fileName.toLowerCase(), file);
          return acc;
        },
        {},
      );
      if (Object.keys(groupedExistingFiles).length > 0) {
        const modalInstance = modal.confirm({
          title: t('modal.fileUploadOptionsForm.title', { count: fileTree.length }),
          footer: null,
          content: (
            <>
              <span>
                <Trans>
                  {t('modal.fileUploadOptionsForm.description', {
                    resource: fileTree[0]?.fileName || 'files',
                    count: fileTree.length,
                  })}
                </Trans>
              </span>
              <FileUploadOptionsForm
                onSubmit={(action) => {
                  uploadOptionAction.current = action;
                  uploadOptionGroupedExistingFiles.current = {};
                  enqueueFiles(fileTree, verifiedTargetFolderUid);
                  modalInstance.destroy();
                }}
                onCancel={() => {
                  modalInstance.destroy();
                }}
              />
            </>
          ),
          centered: true,
        });
        return;
      }
      enqueueFiles(fileTree, verifiedTargetFolderUid);
    },
    [modal, t],
  );

  /**
   * Subscribe a callback to be notified whenever a new file is uploaded successfully.
   */
  const onNewFileUploaded = (callback: (file: FileInterface, isReplacing: boolean) => void) => {
    newFileUploadedSubscribers.current.add(callback);
  };

  /**
   * Unsubscribe a previously subscribed callback from receiving new upload notifications.
   */
  const offNewFileUploaded = (callback: (file: FileInterface, isReplacing: boolean) => void) => {
    newFileUploadedSubscribers.current.delete(callback);
  };

  /**
   * Notify all subscribers of a newly uploaded file (with isUnRead mark and replacement flag).
   */
  const handleFileUploaded = (file: FileInterface, isReplacing: boolean) => {
    const newUploadedFile = { ...file, isUnRead: true };
    newFileUploadedSubscribers.current.forEach((callback) =>
      callback(newUploadedFile, isReplacing),
    );
  };

  /**
   * Update a field in quickShareForm state.
   */
  const handleQuickShareInputChange = (field: keyof QuickShareFormState, value: any): void => {
    switch (field) {
      default:
        setQuickShareForm((prevForm) => ({ ...prevForm, [field]: value }));
        break;
    }
  };

  /**
   * Build a UploadFile[] tree from user-selected files or drag-drop items, filtering .shortcut files.
   */
  const getFileTree = async (items: FileList | DataTransferItemList) => {
    let files: File[] = [];
    if (items instanceof FileList) {
      files = Array.from(items);
    } else {
      files = await traverseFileTree(items);
    }
    files = files.filter((file) => !file.name.toLowerCase().endsWith('.shortcut'));
    return buildFileTree(files);
  };

  /**
   * Recursively filters out 0-byte files from a tree and removes folders that end up empty.
   * Used so 0-byte files are never enqueued for upload.
   */
  const filterZeroByteFromTree = (nodes: UploadFile[]): UploadFile[] => {
    const result: UploadFile[] = [];
    for (const node of nodes) {
      if (node.isFolder && node.children) {
        const filteredChildren = filterZeroByteFromTree(node.children);
        if (filteredChildren.length > 0) {
          result.push({ ...node, children: filteredChildren });
        }
      } else if (node.file) {
        if (node.file.size > 0) {
          result.push(node);
        }
      }
    }
    return result;
  };

  /**
   * Counts file leaves (non-folder nodes with .file) in a tree. Used to detect "no files to upload" after filtering 0-byte.
   */
  const countFileLeaves = (nodes: UploadFile[]): number => {
    let count = 0;
    for (const node of nodes) {
      if (node.isFolder && node.children) {
        count += countFileLeaves(node.children);
      } else if (node.file) {
        count += 1;
      }
    }
    return count;
  };

  /**
   * Top-level method to handle file-selection or drag-drop (FileList/DataTransferItemList/UploadFile[]), opening upload options modal when needed.
   */
  const uploadFiles = async (
    items: FileList | DataTransferItemList | UploadFile[],
    skipTreeBuild?: boolean,
    folderUid?: number,
    cufi?: string,
    isDestinationFolderPasswordProtected?: boolean,
    destinationFolderName?: string,
  ): Promise<void> => {
    // Resolve the intended destination folder
    const targetFolderUid: number | null =
      typeof folderUid === 'number' ? folderUid : (currentFolderUid ?? null);

    // Optimized path for direct upload of provided UploadFile[]
    if (Array.isArray(items) && skipTreeBuild) {
      const itemsFiltered = filterZeroByteFromTree(items);
      if (countFileLeaves(itemsFiltered) === 0) {
        notification.warning({
          message: t('errors.zeroByteFileNotUploaded'),
        });
        return;
      }
      if (
        isDestinationFolderPasswordProtected &&
        targetFolderUid != null &&
        !getFilePasswordSession(targetFolderUid)
      ) {
        pendingUploadAfterPasswordRef.current = {
          fileTree: itemsFiltered,
          targetFolderUid,
          skipExistingCheck: true,
        };
        setPendingFilePasswordRequest({
          fileId: targetFolderUid,
          fileName: destinationFolderName ?? 'Folder',
        });
        setPendingFilePasswordFromMoveForm(true);
        setPendingFilePasswordFromUpload(true);
        return;
      }
      if (cufi) {
        itemsFiltered.forEach((item) => {
          item.cufi = cufi;
        });
      }
      enqueueFiles(itemsFiltered, targetFolderUid);
      return;
    }

    // Convert FileList/DataTransferItemList to file tree as needed
    const fileTree = await getFileTree(items as FileList | DataTransferItemList);
    const fileTreeFiltered = filterZeroByteFromTree(fileTree);
    if (countFileLeaves(fileTreeFiltered) === 0) {
      notification.warning({
        message: t('errors.zeroByteFileNotUploaded'),
      });
      return;
    }
    let verifiedTargetFolderUid = targetFolderUid;

    if (cufi) {
      if (
        isDestinationFolderPasswordProtected &&
        verifiedTargetFolderUid != null &&
        !getFilePasswordSession(verifiedTargetFolderUid)
      ) {
        pendingUploadAfterPasswordRef.current = {
          fileTree: fileTreeFiltered,
          targetFolderUid: verifiedTargetFolderUid,
          skipExistingCheck: true,
        };
        setPendingFilePasswordRequest({
          fileId: verifiedTargetFolderUid,
          fileName: destinationFolderName ?? 'Folder',
        });
        setPendingFilePasswordFromMoveForm(true);
        setPendingFilePasswordFromUpload(true);
        return;
      }
      fileTreeFiltered.forEach((item) => {
        item.cufi = cufi;
      });
      enqueueFiles(fileTreeFiltered, verifiedTargetFolderUid);
      return;
    }

    // Resolve and verify the provided/found folder
    if (verifiedTargetFolderUid && isNaN(Number(verifiedTargetFolderUid))) {
      try {
        const response = await fileService.getFile(verifiedTargetFolderUid as any);
        if (response?.isFolder) {
          verifiedTargetFolderUid = response.uid as number;
        } else {
          notification.error({ title: t('errors.invalidFolder') });
          return;
        }
      } catch {
        notification.error({ title: t('errors.invalidFolderUpload') });
        setUploadFilesQueue([]);
        return;
      }
    } else if (typeof verifiedTargetFolderUid === 'number') {
      verifiedTargetFolderUid = Number(verifiedTargetFolderUid);
    } else {
      verifiedTargetFolderUid = null;
    }

    if (
      isDestinationFolderPasswordProtected &&
      verifiedTargetFolderUid != null &&
      !getFilePasswordSession(verifiedTargetFolderUid)
    ) {
      pendingUploadAfterPasswordRef.current = {
        fileTree: fileTreeFiltered,
        targetFolderUid: verifiedTargetFolderUid,
        skipExistingCheck: false,
      };
      setPendingFilePasswordRequest({
        fileId: verifiedTargetFolderUid,
        fileName: destinationFolderName ?? 'Folder',
      });
      setPendingFilePasswordFromMoveForm(true);
      setPendingFilePasswordFromUpload(true);
      return;
    }

    await checkExistingAndEnqueueOrShowModal(fileTreeFiltered, verifiedTargetFolderUid);
  };

  useEffect(() => {
    if (pendingFilePasswordRequest !== null) return;
    const pending = pendingUploadAfterPasswordRef.current;
    if (!pending) return;

    const hasSession =
      pending.targetFolderUid != null && getFilePasswordSession(pending.targetFolderUid) != null;
    if (hasSession) {
      if (pending.skipExistingCheck) {
        enqueueFiles(pending.fileTree, pending.targetFolderUid);
      } else {
        checkExistingAndEnqueueOrShowModal(pending.fileTree, pending.targetFolderUid);
      }
    }

    pendingUploadAfterPasswordRef.current = null;
    setPendingFilePasswordFromUpload(false);
    setPendingFilePasswordFromMoveForm(false);
  }, [
    pendingFilePasswordRequest,
    getFilePasswordSession,
    checkExistingAndEnqueueOrShowModal,
    setPendingFilePasswordFromUpload,
    setPendingFilePasswordFromMoveForm,
  ]);

  /**
   * Uploads a file or folder, splitting files into chunks and handling error and retry logic.
   * How it behaves depends on upload options (replace mode), as well as actual file/folder handling.
   */
  const uploadFileTask = async (file: UploadFile) => {
    // Don't attempt if already uploading, retry limit reached, or missing file name
    if (file.status === FileUploadStatusEnum.Uploading || file.retries >= 3 || !file.fileName) {
      return;
    }

    // Enforce file size limits using maxFileSizeLic and maxFileSizeSet from authenticated user
    // This is a safety check; files should already be validated in enqueueFiles
    if (!file.isFolder && file.file) {
      const fileSize = file.file.size;

      // Check license limit first (has priority)
      if (authenticatedUser?.maxFileSizeLic) {
        const licenseLimitBytes = authenticatedUser.maxFileSizeLic * 1024 * 1024;
        if (fileSize > licenseLimitBytes) {
          setUploadFilesQueue((prevQueue) =>
            prevQueue.map((u) =>
              u.id === file.id
                ? {
                    ...u,
                    status: FileUploadStatusEnum.Failed,
                    error: t('errors.fileSizeExceedsLicenseLimit'),
                    retries: 3,
                    skipRetryDisplay: true,
                  }
                : u,
            ),
          );
          return;
        }
      }

      // Check admin limit
      if (authenticatedUser?.maxFileSizeSet) {
        const adminLimitBytes = authenticatedUser.maxFileSizeSet * 1024 * 1024;
        if (fileSize > adminLimitBytes) {
          setUploadFilesQueue((prevQueue) =>
            prevQueue.map((u) =>
              u.id === file.id
                ? {
                    ...u,
                    status: FileUploadStatusEnum.Failed,
                    error: t('errors.fileSizeExceedsAdminLimit'),
                    retries: 3,
                    skipRetryDisplay: true,
                  }
                : u,
            ),
          );
          return;
        }
      }
    }

    setUploadFilesQueue((prevQueue) =>
      prevQueue.map((u) =>
        u.id === file.id ? { ...u, status: FileUploadStatusEnum.Uploading } : u,
      ),
    );

    try {
      let isReplacing = false;

      const existingFileLookup = await (async (): Promise<FileInterface | undefined> => {
        if (
          uploadOptionAction.current !== UploadFileActionEnum.NewOnly &&
          uploadOptionAction.current !== UploadFileActionEnum.Replace
        ) {
          return undefined;
        }

        const existingFiles = await fileService.checkFilesExists(
          [file.fileName!],
          file.parentUid ?? null,
        );
        return existingFiles.find(
          (existingItem) => existingItem.fileName.toLowerCase() === file.fileName!.toLowerCase(),
        );
      })();

      if (existingFileLookup) {
        const resolvedExistingFile: FileInterface = existingFileLookup;
        incrementGroupedExistingFileEntry(
          uploadOptionGroupedExistingFiles.current,
          file.fileName!.toLowerCase(),
          resolvedExistingFile,
        );
        isReplacing = uploadOptionAction.current === UploadFileActionEnum.Replace;
      }

      // Folders: may need to be skipped or created (and children updated accordingly)
      if (file.isFolder) {
        if (uploadOptionAction.current === UploadFileActionEnum.NewOnly && existingFileLookup) {
          const resolvedExistingFile: FileInterface = existingFileLookup;
          file.createdFile = resolvedExistingFile;
          file.skipUpload = true;

          if (file.children) {
            file.children.forEach((child) => {
              child.parentCufi = file.createdFile?.cufi;
              child.parentUid = file.createdFile?.id;
            });
          }

          setUploadFilesQueue((prevQueue) => {
            const folderExists = prevQueue.some((u) => u.id === file.id);
            if (!folderExists) {
              return [
                ...prevQueue,
                {
                  ...file,
                  status: FileUploadStatusEnum.Completed,
                  createdFile: resolvedExistingFile,
                },
              ];
            } else {
              return prevQueue.map((u) =>
                u.id === file.id
                  ? {
                      ...u,
                      status: FileUploadStatusEnum.Completed,
                      createdFile: resolvedExistingFile,
                    }
                  : u,
              );
            }
          });

          return;
        }

        if ((file.skipUpload || isReplacing) && existingFileLookup) {
          const resolvedExistingFile: FileInterface = existingFileLookup;
          file.createdFile = resolvedExistingFile;

          if (file.children) {
            file.children.forEach((child) => {
              child.parentCufi = file.createdFile?.cufi;
              child.parentUid = file.createdFile?.id;
            });
          }

          setUploadFilesQueue((prevQueue) => {
            const folderExists = prevQueue.some((u) => u.id === file.id);
            if (!folderExists) {
              return [
                ...prevQueue,
                {
                  ...file,
                  status: FileUploadStatusEnum.Completed,
                  createdFile: resolvedExistingFile,
                },
              ];
            } else {
              return prevQueue.map((u) =>
                u.id === file.id
                  ? {
                      ...u,
                      status: FileUploadStatusEnum.Completed,
                      createdFile: resolvedExistingFile,
                    }
                  : u,
              );
            }
          });

          return;
        }

        // Folder does not exist on backend: actually create it.
        const newFolderPayload: CreateFolderPayload = {
          name: file.fileName,
          parent:
            (file?.parentCufi && String(file.parentCufi)) ||
            (file?.parentUid != null ? String(file.parentUid) : ''),
          correlationId: file.correlationId,
        };

        const response = await fileService.createFolder(newFolderPayload);

        if (cancelledUploadIdsRef.current.has(file.id)) {
          return;
        }
        if (!response.id) {
          markSubtreeFailed(file, t('errors.uploadingFileError'));
          return;
        }

        file.createdFile = response;
        handleFileUploaded(response as FileInterface, isReplacing);

        if (file.children) {
          file.children.forEach((child) => {
            child.parentCufi = response.cufi;
            child.parentUid = response.id;
          });
        }

        setUploadFilesQueue((prevQueue) =>
          prevQueue.map((u) =>
            u.id === file.id
              ? { ...u, status: FileUploadStatusEnum.Completed, createdFile: response }
              : u,
          ),
        );
      } else if (file.file) {
        const uploadBlob = file.file;

        // Files: skip upload entirely if "NewOnly" mode and a file already exists there
        if (uploadOptionAction.current === UploadFileActionEnum.NewOnly && existingFileLookup) {
          const resolvedExistingFile: FileInterface = existingFileLookup;
          setUploadFilesQueue((prevQueue) =>
            prevQueue.map((u) =>
              u.id === file.id
                ? {
                    ...u,
                    status: FileUploadStatusEnum.Completed,
                    progress: 100,
                    createdFile: resolvedExistingFile,
                    cufi: resolvedExistingFile.cufi,
                  }
                : u,
            ),
          );
          return;
        }

        const totalChunks = Math.ceil(uploadBlob.size / CHUNK_SIZE);
        const totalSize = uploadBlob.size;

        const abortController = new AbortController();
        uploadAbortControllersRef.current.set(file.id, abortController);

        try {
          const startChunkIndex = file.chunksUploaded ?? 0;
          const sha256Hasher = await createIncrementalSha256Hasher();

          // Resume: feed already-uploaded chunks into the hasher without re-uploading them.
          for (let replayIndex = 0; replayIndex < startChunkIndex; replayIndex++) {
            const replayStart = replayIndex * CHUNK_SIZE;
            const replayEnd = Math.min(replayStart + CHUNK_SIZE, uploadBlob.size);
            const replayBuffer = await uploadBlob.slice(replayStart, replayEnd).arrayBuffer();
            sha256Hasher.update(replayBuffer);
          }

          for (let chunkIndex = startChunkIndex; chunkIndex < totalChunks; chunkIndex++) {
            const start = chunkIndex * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, uploadBlob.size);
            const chunkBuffer = await uploadBlob.slice(start, end).arrayBuffer();
            sha256Hasher.update(chunkBuffer);
            const blockId = `${chunkIndex}`;
            const isLastChunk = chunkIndex === totalChunks - 1;
            const formData = new FormData();
            formData.append('file', new Blob([chunkBuffer]), file.fileName);
            formData.append('blockId', blockId);
            formData.append('isLastChunk', isLastChunk ? 'true' : 'false');
            if (file.fileName) formData.append('filename', file.fileName);
            // Replace: send the existing file's real cufi (not numeric id). Sending id makes the
            // transfer API treat the request as a continuation session → CHUNK_OUT_OF_ORDER,
            // especially on multi-chunk uploads where blockId must match an active cufi session.
            if (file.cufi) formData.append('cufi', file.cufi);
            else if (isReplacing && existingFileLookup) {
              const resolvedExistingFile: FileInterface = existingFileLookup;
              formData.append('cufi', resolvedExistingFile.cufi);
            }
            if (file.parentUid) formData.append('parentId', String(file.parentUid));
            if (file.correlationId) formData.append('correlationId', file.correlationId);

            let uploadOptions: TransferUploadOptions | undefined;
            if (isLastChunk) {
              const contentHash = sha256Hasher.digestBase64();
              file.contentHash = contentHash;
              formData.append('contentHash', contentHash);
              uploadOptions = { contentHash };
              setUploadFilesQueue((prevQueue) =>
                prevQueue.map((uploadItem) =>
                  uploadItem.id === file.id ? { ...uploadItem, contentHash } : uploadItem,
                ),
              );
            }

            const response: UploadResponse = await transferService.upload(
              formData,
              (progressEvent) => {
                if (progressEvent.lengthComputable) {
                  const bytesUploadedInChunk = progressEvent.loaded;
                  const bytesUploadedBeforeChunk = chunkIndex * CHUNK_SIZE;
                  const totalBytesUploaded = bytesUploadedBeforeChunk + bytesUploadedInChunk;
                  const percentComplete = (totalBytesUploaded / totalSize) * 100;
                  setUploadFilesQueue((prevQueue) =>
                    prevQueue.map((u) =>
                      u.id === file.id
                        ? {
                            ...u,
                            progress: Math.min(100, Math.floor(percentComplete)),
                          }
                        : u,
                    ),
                  );
                }
              },
              abortController.signal,
              uploadOptions,
            );

            // Handle backend response per chunk (for chunked upload)
            if ('cufi' in response && 'version' in response) {
              if (response.cufi) {
                file.cufi = response.cufi;
                setUploadFilesQueue((prevQueue) =>
                  prevQueue.map((u) => (u.id === file.id ? { ...u, cufi: response.cufi } : u)),
                );
              }
            } else {
              // Last chunk is always a file object (FileInterface)
              setUploadFilesQueue((prevQueue) =>
                prevQueue.map((u) => (u.id === file.id ? { ...u, createdFile: response } : u)),
              );
              handleFileUploaded(response as FileInterface, isReplacing);
            }

            setUploadFilesQueue((prevQueue) =>
              prevQueue.map((u) =>
                u.id === file.id
                  ? {
                      ...u,
                      progress: Math.floor(((chunkIndex + 1) / totalChunks) * 100),
                      chunksUploaded: chunkIndex + 1,
                      totalChunks,
                    }
                  : u,
              ),
            );
          }

          setUploadFilesQueue((prevQueue) =>
            prevQueue.map((u) =>
              u.id === file.id ? { ...u, status: FileUploadStatusEnum.Completed } : u,
            ),
          );
        } finally {
          uploadAbortControllersRef.current.delete(file.id);
        }
      }
    } catch (error: any) {
      if (error?.aborted === true) {
        uploadAbortControllersRef.current.delete(file.id);
        return;
      }

      if (isTransientNetworkError(error)) {
        setUploadFilesQueue((prevQueue) =>
          prevQueue.map((uploadQueueItem) =>
            uploadQueueItem.id === file.id
              ? { ...uploadQueueItem, status: FileUploadStatusEnum.Pending }
              : uploadQueueItem,
          ),
        );
        return;
      }
      console.error(error.response);

      // Extract error response - can be string, object, or nested in response property
      const errorResponse =
        error.response ||
        (typeof error === 'string' ? error : undefined) ||
        (error?.data ? error.data : undefined);

      const apiErrorCode = getApiErrorCode(errorResponse);
      const isFileTypeRestrictionError = apiErrorCode === ApiErrorEnum.FileTypeRestriction;
      const isFileAlreadyInUseError = apiErrorCode === ApiErrorEnum.FileAlreadyInUse;
      const isContentHashMismatchError = apiErrorCode === ApiErrorEnum.ContentHashMismatch;

      // On error, mark subtree if it's a folder, or file as failed.
      if (file.isFolder) {
        const errorMessage = isFileTypeRestrictionError
          ? t('errors.fileTypeRestrictionError')
          : t('errors.uploadingFileError');
        markSubtreeFailed(file, errorMessage);
        Sentry.captureException(error);
        return;
      }

      // For files, handle FILE_TYPE_RESTRICTION with immediate failure (retries = 3)
      if (isFileTypeRestrictionError) {
        setUploadFilesQueue((prevQueue) =>
          prevQueue.map((u) =>
            u.id === file.id
              ? {
                  ...u,
                  retries: 3,
                  skipRetryDisplay: true,
                  status: FileUploadStatusEnum.Failed,
                  error: t('errors.fileTypeRestrictionError'),
                }
              : u,
          ),
        );
        return;
      }

      // For files, handle FILE_ALREADY_IN_USE with immediate failure (retries = 3)
      if (isFileAlreadyInUseError) {
        setUploadFilesQueue((prevQueue) =>
          prevQueue.map((u) =>
            u.id === file.id
              ? {
                  ...u,
                  retries: 3,
                  skipRetryDisplay: true,
                  status: FileUploadStatusEnum.Failed,
                  error: t('errors.fileAlreadyInUseError'),
                }
              : u,
          ),
        );
        return;
      }

      // For other errors, use normal retry logic
      const fallbackErrorMessage =
        typeof error === 'string'
          ? error
          : error instanceof Error
            ? error.message
            : t('errors.uploadingFileError');

      setUploadFilesQueue((prevQueue) =>
        prevQueue.map((uploadQueueItem) => {
          if (uploadQueueItem.id !== file.id) return uploadQueueItem;
          const nextRetry = uploadQueueItem.retries + 1;
          const isFinalAttempt = nextRetry >= 3;
          return {
            ...uploadQueueItem,
            retries: nextRetry,
            status: FileUploadStatusEnum.Failed,
            error:
              isContentHashMismatchError && isFinalAttempt
                ? t('errors.contentHashMismatchError')
                : nextRetry < 3
                  ? t('errors.uploadingFileError')
                  : fallbackErrorMessage,
          };
        }),
      );
    }
  };

  /**
   * Attempts to start the next available uploads. Only launches up to MAX_CONCURRENT_UPLOADS.
   * Only files whose parent (if any) is completed are eligible to be started.
   */
  function tryStartUploads() {
    if (!isNetworkAvailable) {
      return;
    }

    const activeUploads = uploadFilesQueue.filter(
      (u) => u.status === FileUploadStatusEnum.Uploading,
    );
    const pendingUploads = uploadFilesQueue.filter(
      (u) =>
        (u.status === FileUploadStatusEnum.Pending || u.status === FileUploadStatusEnum.Failed) &&
        u.retries < 3,
    );

    const readyToUpload = pendingUploads.filter((file) => {
      // Special logic: allow files with parentUid referring to missing parent (e.g. QuickShare root)
      if (file.parentUid && !uploadFilesQueue.some((u) => u.id === String(file.parentUid))) {
        return true;
      }
      if (!file.parentUUID && !file.parentUid) return true;
      const parent = uploadFilesQueue.find(
        (u) => u.id === file.parentUUID || u.id === String(file.parentUid),
      );
      return parent && parent.status === FileUploadStatusEnum.Completed;
    });
    const slotsAvailable = MAX_CONCURRENT_UPLOADS - activeUploads.length;
    readyToUpload.slice(0, slotsAvailable).forEach((uploadFile) => {
      uploadFileTask(uploadFile);
    });
  }
  tryStartUploadsRef.current = tryStartUploads;

  /**
   * Utility to check if this UploadFile is a direct child of a folder (vs root).
   */
  const checkIfFileIsInsideFolder = (file: UploadFile): boolean => {
    return (
      file.parentCufi !== null &&
      file.parentCufi !== undefined &&
      file.parentCufi !== '' &&
      !file.isFolder
    );
  };

  /**
   * Find all failed UploadFile nodes whose ancestors include a given folder UUID.
   * Returns flat array of failed items under that branch.
   */
  const getFailedFilesInFolder = (folderUUID: string): UploadFile[] => {
    const failedFiles: UploadFile[] = [];

    // Recursively traverse the tree rooted at folderUUID
    const traverse = (currentUUID: string) => {
      const children = uploadFilesQueue.filter((u) => u.parentUUID === currentUUID);

      children.forEach((child) => {
        if (child.isFolder) {
          traverse(child.id);
        } else {
          if (
            child.status === FileUploadStatusEnum.Failed ||
            child.status === FileUploadStatusEnum.Canceled
          ) {
            failedFiles.push(child);
          }
        }
      });
    };

    traverse(folderUUID);
    return failedFiles;
  };

  /**
   * Group top-level files and/or folders for status summary, progress, etc. Internal recursive grouping is depth- and context-aware.
   */
  function groupUploadItems(
    uploadFilesQueue: UploadFile[],
    onlyQuickShare = false,
    startDepth = 0,
  ): GroupedUploadItem[] {
    /**
     * Recursively collect GroupedUploadItem[] at required depth in the hierarchy.
     */
    function collectItemsAtDepth(parentUUID: string | null, depth: number): GroupedUploadItem[] {
      let childItems: UploadFile[] = [];
      if (onlyQuickShare) {
        childItems = uploadFilesQueue.filter(
          (item) => item.parentUUID === parentUUID && item.isQuickShare,
        );
      } else {
        childItems = uploadFilesQueue.filter(
          (item) => item.parentUUID === parentUUID && !item.isQuickShare,
        );
      }

      // Ignore all items at this depth if not yet at startDepth
      if (depth < startDepth) {
        const deeperItems: GroupedUploadItem[] = [];
        for (const item of childItems) {
          if (item.isFolder) {
            deeperItems.push(...collectItemsAtDepth(item.id, depth + 1));
          }
        }
        return deeperItems;
      }

      const items: GroupedUploadItem[] = [];

      // Helper to summarize file/folder state for folders
      function collectFolderFiles(folderUUID: string): {
        total: number;
        totalSize: number;
        uploaded: number;
        cumulativeProgress: number;
        statuses: Set<FileUploadStatusEnum>;
      } {
        let total = 0;
        let totalSize = 0;
        let uploaded = 0;
        let cumulativeProgress = 0;
        const statuses = new Set<FileUploadStatusEnum>();
        let folderChildItems = uploadFilesQueue.filter((item) => item.parentUUID === folderUUID);
        if (onlyQuickShare) {
          folderChildItems = folderChildItems.filter((item) => item.isQuickShare);
        }

        for (const item of folderChildItems) {
          if (item.isFolder) {
            const subFolder = collectFolderFiles(item.id);
            total += subFolder.total;
            totalSize += subFolder.totalSize;
            uploaded += subFolder.uploaded;
            cumulativeProgress += subFolder.cumulativeProgress;
            subFolder.statuses.forEach((s) => statuses.add(s));
          } else {
            total += 1;
            totalSize += item.file?.size || 0;
            statuses.add(item.status);
            cumulativeProgress += item.progress || 0;
            if (item.status === FileUploadStatusEnum.Completed) uploaded += 1;
          }
        }
        return { total, totalSize, uploaded, cumulativeProgress, statuses };
      }

      // Convert top-level files and folders into GroupedUploadItem[] for UI/summary
      for (const item of childItems) {
        if (item.isFolder) {
          const { total, totalSize, uploaded, cumulativeProgress, statuses } = collectFolderFiles(
            item.id,
          );

          const progress = total > 0 ? Math.floor(cumulativeProgress / total) : 0;

          let folderStatus: FileUploadStatusEnum;
          if (
            statuses.has(FileUploadStatusEnum.Pending) ||
            statuses.has(FileUploadStatusEnum.Uploading)
          ) {
            folderStatus = item.isQuickShare
              ? FileUploadStatusEnum.Pending
              : FileUploadStatusEnum.Uploading;
          } else if (statuses.has(FileUploadStatusEnum.Canceled)) {
            folderStatus = FileUploadStatusEnum.Canceled;
          } else if (statuses.has(FileUploadStatusEnum.Failed)) {
            folderStatus = FileUploadStatusEnum.Failed;
          } else {
            folderStatus = FileUploadStatusEnum.Completed;
          }

          items.push({
            id: item.id,
            isFolder: true,
            fileName: item.fileName || '',
            fileExtension: '',
            status: folderStatus,
            totalFiles: total,
            uploadedFiles: uploaded,
            fileSize: totalSize,
            progress: progress,
            createdFile: item.createdFile,
            retries: item.retries,
            skipRetryDisplay: item.skipRetryDisplay,
          });
        } else {
          const progress = item.progress || 0;
          const fileExtension = item.fileName?.split('.').pop() || '';

          items.push({
            id: item.id,
            isFolder: false,
            fileName: item.fileName || '',
            fileExtension: fileExtension || item?.createdFile?.fileExtension || '',
            status: item.status,
            fileSize: item.file?.size || 0,
            progress,
            createdFile: item.createdFile,
            retries: item.retries,
            skipRetryDisplay: item.skipRetryDisplay,
            totalFiles: 1,
            uploadedFiles: item.status === FileUploadStatusEnum.Completed ? 1 : 0,
          });
        }
      }
      return items;
    }

    // Begin grouping from root (parentUUID==null) and requested depth
    return collectItemsAtDepth(null, 0);
  }

  /**
   * Reset all failed uploads to Pending (may filter for onlyQuickShare).
   */
  const retryFailedUploads = (onlyQuickShare: boolean = false): void => {
    setUploadFilesQueue((prevQueue) =>
      prevQueue.map((file) => {
        if (
          file.isQuickShare === onlyQuickShare &&
          (file.status === FileUploadStatusEnum.Failed ||
            file.status === FileUploadStatusEnum.Canceled)
        ) {
          return {
            ...file,
            status: FileUploadStatusEnum.Pending,
            retries: 0,
          };
        }
        return file;
      }),
    );
  };

  /**
   * Remove all uploads except Quick Share ones from state.
   */
  const handleEmptyUploadFilesQueue = () => {
    setUploadFilesQueue((prevQueue) => prevQueue.filter((file) => file.isQuickShare));
  };

  /**
   * Remove all Quick Share uploads from state, also reset form to default.
   */
  const handleEmptyQuickShareUploadFilesQueue = () => {
    setQuickShareForm({
      users: [],
      groups: [],
      permission: '0',
      expiration: '1',
      hasMessage: true,
      message: '',
    });
    setUploadFilesQueue((prevQueue) => prevQueue.filter((file) => !file.isQuickShare));
  };

  /**
   * Complete the Quick Share workflow: assign permissions/expiration/message to quick share files via API, notify user.
   */
  const handleShareQuickShareFiles = () => {
    const fileIds = uploadQuickShareGroupedFilesQueue
      .map((f) => f.createdFile?.cufi)
      .filter(Boolean) as string[];

    const days = quickShareForm?.expiration ? parseInt(quickShareForm.expiration, 10) : 0;
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + days);
    expirationDate.setHours(23, 59, 59, 999);
    expirationDate.setMilliseconds(0);
    const expirationTicks = dateToTicks(expirationDate);

    const request: UpdateFilesAccessPayload = {
      allRecipients: [
        ...quickShareForm?.users?.map((u) => ({
          recipient: u.email,
          isGroup: false,
        })),
        ...quickShareForm?.groups?.map((g) => ({
          recipient: g.name,
          isGroup: true,
        })),
      ],
      files: fileIds,
      limitations: {
        expiry: expirationTicks,
        readOnly: quickShareForm?.permission === '0',
      },
    };
    if (quickShareForm?.hasMessage && quickShareForm?.message?.trim() !== '') {
      request.message = quickShareForm.message;
    } else if (quickShareForm?.hasMessage) {
      request.message = '';
    } else {
      request.message = null;
    }

    fileService
      .updateFilesAccess(request)
      .then(() => {
        notification.success({ title: t('notifications.quickShareSuccess') });
        setUploadFilesQueue((prevQueue) =>
          prevQueue.map((file) =>
            file.isQuickShare
              ? {
                  ...file,
                  status:
                    file.status === FileUploadStatusEnum.FailedSharing
                      ? FileUploadStatusEnum.Completed
                      : file.status,
                }
              : file,
          ),
        );
        // We do *not* automatically clear quickshare queue here.
      })
      .catch(() => {
        setUploadFilesQueue((prevQueue) =>
          prevQueue.map((file) =>
            file.isQuickShare
              ? {
                  ...file,
                  status:
                    file.status === FileUploadStatusEnum.Completed
                      ? FileUploadStatusEnum.FailedSharing
                      : file.status,
                }
              : file,
          ),
        );
        notification.error({ title: t('errors.quickShareError') });
      });
  };

  /**
   * Show modal with upload errors for either all or only QuickShare files depending on flag.
   */
  const handleOpenUploadingFilesErrorsModal = (onlyQuickShareFiles: boolean): void => {
    setShowOnlyQuickShareFilesUploadingErrors(onlyQuickShareFiles);
    modal.error({
      title: t('common.uploadErrors'),
      footer: null,
      content: (
        <>
          <span>
            <Trans>{t('common.uploadErrorsDescription')}</Trans>
          </span>
          <FilesUploadErrorForm
            uploadFiles={failedUploadFiles}
            onCancel={() => {
              setShowOnlyQuickShareFilesUploadingErrors(false);
              Modal.destroyAll();
            }}
          />
        </>
      ),
    });
  };

  /**
   * Memo: How many files are currently uploading (not pending/failed/complete), for main UI.
   */
  const uploadingFilesAmount: number = useMemo(() => {
    return uploadFilesQueue.filter((file) => file.status === FileUploadStatusEnum.Uploading).length;
  }, [uploadFilesQueue]);

  /**
   * Memo: How many files have been successfully uploaded.
   */
  const uploadedFilesAmount: number = useMemo(() => {
    return uploadFilesQueue.filter((file) => file.status === FileUploadStatusEnum.Completed).length;
  }, [uploadFilesQueue]);

  /**
   * Memo: True if *any* file is currently uploading (Uploading or Pending).
   */
  const isUploadingFiles = useMemo(() => {
    return uploadFilesQueue.some(
      (file) =>
        file.status === FileUploadStatusEnum.Uploading ||
        file.status === FileUploadStatusEnum.Pending,
    );
  }, [uploadFilesQueue]);

  /**
   * Memo: True if *any* quick share file is uploading (Uploading or Pending).
   */
  const isUploadingQuickShareFiles = useMemo(() => {
    return uploadFilesQueue.some(
      (file) =>
        (file.status === FileUploadStatusEnum.Uploading ||
          file.status === FileUploadStatusEnum.Pending) &&
        file.isQuickShare,
    );
  }, [uploadFilesQueue]);

  /**
   * Memo: True if any folder is present in the queue.
   */
  const areFoldersPresentsInFilesQueue: boolean = useMemo(() => {
    return uploadFilesQueue.some((file) => file.isFolder);
  }, [uploadFilesQueue]);

  /**
   * Memo: Hierarchical grouped upload summary (for regular uploads).
   */
  const uploadGroupedFilesQueue: GroupedUploadItem[] = useMemo(() => {
    return groupUploadItems(uploadFilesQueue);
  }, [uploadFilesQueue]);

  /**
   * Memo: Hierarchical grouped upload summary (for quick share uploads).
   */
  const uploadQuickShareGroupedFilesQueue: GroupedUploadItem[] = useMemo(() => {
    return groupUploadItems(uploadFilesQueue, true);
  }, [uploadFilesQueue]);

  /**
   * Memo: Grouped upload summary for quick share, omitting root folder node if only one present.
   */
  const uploadQuickShareGroupedFilesQueueWithoutRootFolder: GroupedUploadItem[] = useMemo(() => {
    const filteredUploadFilesQueue = uploadFilesQueue.filter((file) => file.isQuickShare);
    let offset = 1;
    if (filteredUploadFilesQueue.length === 1) {
      offset = 0;
    }
    return groupUploadItems(uploadFilesQueue, true, offset);
  }, [uploadFilesQueue]);

  /**
   * Memo: How many total files are present in all grouped upload queues.
   */
  const totalGroupedFilesAmount: number = useMemo(() => {
    return uploadGroupedFilesQueue.reduce((acc, item) => acc + item.totalFiles, 0);
  }, [uploadGroupedFilesQueue]);

  /**
   * Memo: How many total quick share files are present in grouped queue.
   */
  const totalQuickShareGroupedFilesAmount: number = useMemo(() => {
    return uploadQuickShareGroupedFilesQueue.reduce((acc, item) => acc + item.totalFiles, 0);
  }, [uploadQuickShareGroupedFilesQueue]);

  /**
   * Memo: How many total grouped files uploaded (all uploads).
   */
  const uploadedGroupedFilesAmount: number = useMemo(() => {
    return uploadGroupedFilesQueue.reduce((acc, item) => acc + item.uploadedFiles, 0);
  }, [uploadGroupedFilesQueue]);

  /**
   * Memo: How many grouped quick share files have been uploaded so far.
   */
  const uploadedQuickShareGroupedFilesAmount: number = useMemo(() => {
    return uploadQuickShareGroupedFilesQueue.reduce((acc, item) => acc + item.uploadedFiles, 0);
  }, [uploadQuickShareGroupedFilesQueue]);

  /**
   * Memo: How many failed files exist, counting both failed folders and failed files.
   */
  const failedGroupedFilesAmount: number = useMemo(() => {
    return uploadGroupedFilesQueue.reduce((acc, item) => {
      if (
        item.isFolder &&
        (item.status === FileUploadStatusEnum.Failed ||
          item.status === FileUploadStatusEnum.Canceled)
      ) {
        return acc + item.totalFiles;
      } else if (
        !item.isFolder &&
        (item.status === FileUploadStatusEnum.Failed ||
          item.status === FileUploadStatusEnum.Canceled)
      ) {
        return acc + 1;
      }
      return acc;
    }, 0);
  }, [uploadGroupedFilesQueue]);

  /**
   * Memo: List of all files that failed uploading (filtered by upload error modal state).
   */
  const failedUploadFiles = useMemo(() => {
    return uploadFilesQueue.filter(
      (file) =>
        (file.status === FileUploadStatusEnum.Failed ||
          file.status === FileUploadStatusEnum.Canceled) &&
        file.isQuickShare === showOnlyQuickShareFilesUploadingErrors,
    );
  }, [uploadFilesQueue, showOnlyQuickShareFilesUploadingErrors]);

  /**
   * Memo: All quick share files with failed status.
   */
  const failedUploadQuickShareFiles: Array<UploadFile> = useMemo(() => {
    return uploadFilesQueue.filter(
      (file) =>
        (file.status === FileUploadStatusEnum.Failed ||
          file.status === FileUploadStatusEnum.Canceled) &&
        file.isQuickShare,
    );
  }, [uploadFilesQueue]);

  /**
   * Memo: True if all quick share files in the queue are completed/uploaded and there are files to share.
   */
  const isQuickShareUploadFilesCompletedWithoutErrors = useMemo(() => {
    return (
      uploadFilesQueue.length > 0 &&
      uploadFilesQueue.every(
        (file) => file.isQuickShare && file.status === FileUploadStatusEnum.Completed,
      )
    );
  }, [uploadFilesQueue]);

  /**
   * Memo: Progress for quick share uploads (by total bytes).
   */
  const quickShareGlobalProgressByBytes = useMemo(() => {
    const files = uploadFilesQueue.filter((f) => f.isQuickShare && !f.isFolder && f.file);
    const totalBytes = files.reduce((acc, file) => acc + (file.file?.size || 0), 0);
    const uploadedBytes = files.reduce(
      (acc, file) => acc + Math.floor(((file.progress || 0) / 100) * (file.file?.size || 0)),
      0,
    );
    if (totalBytes === 0) return 0;
    return Math.floor((uploadedBytes / totalBytes) * 100);
  }, [uploadFilesQueue]);

  /**
   * When logged out, clear both queues.
   * When all quick share files have uploaded, auto-trigger sharing.
   */
  useEffect(() => {
    if (!isLoggedIn) {
      handleEmptyQuickShareUploadFilesQueue();
      handleEmptyUploadFilesQueue();
      void purgePersistedUploadQueue();
    }

    const areAllQuickShareFilesUploaded =
      uploadFilesQueue.length > 0 &&
      uploadFilesQueue.every(
        (file) => file.isQuickShare && file.status === FileUploadStatusEnum.Completed,
      );

    if (areAllQuickShareFilesUploaded) {
      handleShareQuickShareFiles();
    }
  }, [
    isLoggedIn,
    isUploadingQuickShareFiles,
    uploadedQuickShareGroupedFilesAmount,
    totalQuickShareGroupedFilesAmount,
  ]);

  /**
   * Warn user if they try to close/navigate tab while files are still uploading!
   */
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const isUploading = uploadFilesQueue.some(
        (file) =>
          file.status === FileUploadStatusEnum.Pending ||
          file.status === FileUploadStatusEnum.Uploading,
      );
      if (isUploading) {
        e.preventDefault();
        // For older browsers
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploadFilesQueue]);

  /**
   * Automatically fire off new uploads whenever the queue changes.
   */
  useEffect(() => {
    tryStartUploadsRef.current();
    scheduleSyncUploadQueueToIdb();
  }, [uploadFilesQueue, isNetworkAvailable]);

  useEffect(() => {
    const handleOffline = () => {
      setIsNetworkAvailable(false);
      pauseUploadsForNetwork();
    };
    const handleOnline = () => {
      setIsNetworkAvailable(true);
      networkPauseRef.current = false;
      if (resumeUploadsTimeoutRef.current != null) {
        window.clearTimeout(resumeUploadsTimeoutRef.current);
      }
      resumeUploadsTimeoutRef.current = window.setTimeout(() => {
        tryStartUploadsRef.current();
        resumeUploadsTimeoutRef.current = null;
      }, 1500);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      if (resumeUploadsTimeoutRef.current != null) {
        window.clearTimeout(resumeUploadsTimeoutRef.current);
      }
      if (syncUploadQueueTimeoutRef.current != null) {
        window.clearTimeout(syncUploadQueueTimeoutRef.current);
      }
    };
  }, [pauseUploadsForNetwork]);

  useEffect(() => {
    if (!isLoggedIn || authenticatedUser?.id == null || hasHydratedUploadQueueRef.current) {
      return;
    }
    hasHydratedUploadQueueRef.current = true;

    void (async () => {
      const loaded = await loadUploadQueue(authenticatedUser.id);
      if (!loaded || loaded.items.length === 0) {
        return;
      }
      uploadSessionIdRef.current = loaded.session.sessionId;
      uploadTargetFolderUidRef.current = loaded.session.targetFolderUid;
      if (loaded.session.isQuickShare) {
        setQuickShareForm(loaded.session.quickShareForm as QuickShareFormState);
      }
      setUploadFilesQueue(loaded.items);
    })();
  }, [authenticatedUser?.id, isLoggedIn]);

  /**
   * When all uploads (non-quick share) are completed, trigger batches refresh if not already polling.
   */
  useEffect(() => {
    // Check if there are any non-quick share files in the queue
    const nonQuickShareFiles = uploadFilesQueue.filter((file) => !file.isQuickShare);
    const hasNonQuickShareFiles = nonQuickShareFiles.length > 0;

    // Check if all non-quick share uploads are completed (no pending or uploading files)
    const allNonQuickShareUploadsCompleted =
      hasNonQuickShareFiles &&
      !nonQuickShareFiles.some(
        (file) =>
          file.status === FileUploadStatusEnum.Pending ||
          file.status === FileUploadStatusEnum.Uploading,
      );

    // If all uploads are completed and we haven't triggered refresh yet, and polling is not active
    if (
      allNonQuickShareUploadsCompleted &&
      !hasTriggeredBatchesRefresh.current &&
      !isBatchesPolling
    ) {
      hasTriggeredBatchesRefresh.current = true;
      refreshBatches();
    }

    // Reset the flag when uploads start again
    if (isUploadingFiles && hasTriggeredBatchesRefresh.current) {
      hasTriggeredBatchesRefresh.current = false;
    }
  }, [isUploadingFiles, uploadFilesQueue, isBatchesPolling, refreshBatches]);

  // Final context provider.
  return (
    <FileUploadContext.Provider
      value={{
        uploadFiles,
        uploadGroupedFilesQueue,
        uploadQuickShareGroupedFilesQueue,
        uploadQuickShareGroupedFilesQueueWithoutRootFolder,
        uploadFilesQueue,
        uploadingFilesAmount,
        uploadedFilesAmount,
        totalGroupedFilesAmount,
        totalQuickShareGroupedFilesAmount,
        uploadedGroupedFilesAmount,
        uploadedQuickShareGroupedFilesAmount,
        failedGroupedFilesAmount,
        isUploadingFiles,
        isUploadingQuickShareFiles,
        areFoldersPresentsInFilesQueue,
        buildFileTreeFromUploadFiles,
        getFilesToEnqueue,
        groupUploadItems,
        getFileTree,
        getFailedFilesInFolder,
        isFileInsideFolder: checkIfFileIsInsideFolder,
        emptyUploadFilesQueue: handleEmptyUploadFilesQueue,
        emptyQuickShareUploadFilesQueue: handleEmptyQuickShareUploadFilesQueue,
        cancelUploadItem,
        onNewFileUploaded,
        offNewFileUploaded,
        quickShareForm,
        handleQuickShareInputChange,
        handleShareQuickShareFiles,
        failedUploadQuickShareFiles,
        handleOpenUploadingFilesErrorsModal,
        retryFailedUploads,
        isQuickShareUploadFilesCompletedWithoutErrors,
        setCurrentFolderUid,
        quickShareGlobalProgressByBytes,
        isNetworkAvailable,
      }}
    >
      {children}
    </FileUploadContext.Provider>
  );
};
